/**
 * @file auth/msalConfig.js
 * @description Microsoft Authentication Library (MSAL) configuration.
 *
 * Reads Azure app registration settings from Vite env variables so the
 * values can be swapped per environment without changing source code.
 *
 * The `loginRequest` object defines which scopes the frontend requests
 * when acquiring a token — it must include the backend API scope so the
 * returned JWT is accepted by the Express passport-azure-ad strategy.
 */

/** @type {import('@azure/msal-browser').Configuration} */
export const msalConfig = {
  auth: {
    clientId:    import.meta.env.VITE_AZURE_CLIENT_ID,
    authority:   `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID}`,
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin,
  },
  cache: {
    // sessionStorage: token survives page refresh but is cleared when tab is closed
    cacheLocation:          'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

/**
 * Scopes requested during interactive login.
 * openid and profile give access to basic user claims.
 * The API scope allows the token to be accepted by the backend.
 */
export const loginRequest = {
  scopes: [
    'openid',
    'profile',
    import.meta.env.VITE_AZURE_API_SCOPE,
  ].filter(Boolean), // filter in case VITE_AZURE_API_SCOPE is not yet set
};

/**
 * Scopes used when silently acquiring a token for API calls.
 * Typically the same as loginRequest.scopes — just the API scope suffices.
 */
export const tokenRequest = {
  scopes: [import.meta.env.VITE_AZURE_API_SCOPE].filter(Boolean),
};
